import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getInteractiveLesson, requestInteractiveHint, saveInteractiveLessonProgress } from '../../services/api';
import { validateLessonData, normalizeLessonData } from '../../utils/lessonSchema';
import LessonTopBar from '../../components/LessonPlayer/LessonTopBar';
import LessonBottomBar from '../../components/LessonPlayer/LessonBottomBar';
import ScreenRenderer from '../../components/LessonPlayer/ScreenRenderer';
import AITutorPanel from '../../components/LessonPlayer/AITutorPanel';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import './InteractiveLesson.css';

const InteractiveLesson = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest } = useApi();
  // Load saved progress immediately on mount, before lesson loads
  const loadSavedProgress = () => {
    if (id) {
      const savedProgress = localStorage.getItem(`lesson-progress-${id}`);
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          return {
            currentScreenIndex: progress.currentScreenIndex || 0,
            userResponses: progress.userResponses || {},
            screenStates: progress.screenStates || {}
          };
        } catch (e) {
          console.error('Failed to load saved progress:', e);
        }
      }
    }
    return {
      currentScreenIndex: 0,
      userResponses: {},
      screenStates: {}
    };
  };

  const initialProgress = loadSavedProgress();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [lesson, setLesson] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(initialProgress.currentScreenIndex);
  const [userResponses, setUserResponses] = useState(initialProgress.userResponses);
  const [screenStates, setScreenStates] = useState(initialProgress.screenStates);
  const [showSummary, setShowSummary] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [hints, setHints] = useState({}); // { screenId: [hint1, hint2, ...] }
  const [hintLoading, setHintLoading] = useState(false);

  // Ensure saved progress is loaded on mount (as a backup to initial state)
  useEffect(() => {
    if (id) {
      const savedProgress = localStorage.getItem(`lesson-progress-${id}`);
      if (savedProgress) {
        try {
          const progress = JSON.parse(savedProgress);
          if (progress.currentScreenIndex !== undefined) {
            setCurrentScreenIndex(progress.currentScreenIndex);
          }
          if (progress.userResponses) {
            setUserResponses(progress.userResponses);
          }
          if (progress.screenStates) {
            setScreenStates(progress.screenStates);
          }
        } catch (e) {
          console.error('Failed to load saved progress on mount:', e);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only run once on mount

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const response = await handleRequest(getInteractiveLesson, id);
        const lessonData = response.data.lessonData;

        // Validate lesson data
        const validation = validateLessonData(lessonData);
        if (!validation.valid) {
          throw new Error(`Invalid lesson data: ${validation.errors.join(', ')}`);
        }

        // Normalize lesson data
        const normalized = normalizeLessonData(lessonData);
        setLesson(response.data);
        setLessonData(normalized);

        // Reload saved progress from localStorage after lesson loads and validate screen index
        const savedProgress = localStorage.getItem(`lesson-progress-${id}`);
        if (savedProgress) {
          try {
            const progress = JSON.parse(savedProgress);
            // Only update if the saved index is valid for this lesson
            if (progress.currentScreenIndex >= 0 && progress.currentScreenIndex < normalized.screens.length) {
              setCurrentScreenIndex(progress.currentScreenIndex);
            } else if (progress.currentScreenIndex >= normalized.screens.length) {
              // If saved index is beyond lesson length, go to last screen
              setCurrentScreenIndex(normalized.screens.length - 1);
            }
            setUserResponses(progress.userResponses || {});
            setScreenStates(progress.screenStates || {});
          } catch (e) {
            console.error('Failed to load saved progress:', e);
          }
        }
      } catch (error) {
        setErrorMessage(error.message || 'Failed to load lesson');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLesson();
    }
  }, [id, handleRequest]);

  // Auto-save progress to localStorage (save immediately, even before lesson loads)
  useEffect(() => {
    if (id) {
      const progress = {
        currentScreenIndex,
        userResponses,
        screenStates
      };
      localStorage.setItem(`lesson-progress-${id}`, JSON.stringify(progress));
    }
  }, [currentScreenIndex, userResponses, screenStates, id]);

  // Auto-save current screen index to database (debounced)
  useEffect(() => {
    if (id && user && lessonData) {
      const timeoutId = setTimeout(async () => {
        try {
          await handleRequest(saveInteractiveLessonProgress, id, {
            currentScreenIndex: currentScreenIndex
          });
        } catch (error) {
          console.error('Failed to save screen index:', error);
        }
      }, 1000); // Debounce: save 1 second after screen change

      return () => clearTimeout(timeoutId);
    }
  }, [currentScreenIndex, id, user, lessonData, handleRequest]);

  const handleAnswer = useCallback(async (screenId, answer, validationResult) => {
    setUserResponses(prev => ({
      ...prev,
      [screenId]: answer
    }));

    setScreenStates(prev => ({
      ...prev,
      [screenId]: {
        answered: true,
        isCorrect: validationResult?.isCorrect || false,
        feedback: validationResult?.feedback || ''
      }
    }));

    // Save response to database
    if (id && user) {
      try {
        await handleRequest(saveInteractiveLessonProgress, id, {
          responses: [{
            screenId,
            response: answer,
            isCorrect: validationResult?.isCorrect || false,
            timestamp: new Date()
          }]
        });
      } catch (error) {
        console.error('Failed to save response:', error);
        // Don't show error to user, just log it
      }
    }
  }, [id, user, handleRequest]);

  const handleNext = useCallback(async () => {
    setShowSolution(false); // Reset solution display when moving to next screen
    if (lessonData && currentScreenIndex < lessonData.screens.length - 1) {
      const newIndex = currentScreenIndex + 1;
      setCurrentScreenIndex(newIndex);
      
      // Save current screen index to database
      if (id && user) {
        try {
          await handleRequest(saveInteractiveLessonProgress, id, {
            currentScreenIndex: newIndex
          });
        } catch (error) {
          console.error('Failed to save progress:', error);
        }
      }
    } else if (currentScreenIndex === lessonData.screens.length - 1) {
      // Show summary and mark lesson as completed
      setShowSummary(true);
      
      // Mark lesson as completed in database
      if (id && user) {
        try {
          // Convert userResponses to array format for database
          const responsesArray = Object.entries(userResponses).map(([screenId, response]) => {
            const screenState = screenStates[screenId];
            return {
              screenId,
              response,
              isCorrect: screenState?.isCorrect || false,
              attempts: screenState?.attempts || 1,
              timestamp: new Date()
            };
          });

          await handleRequest(saveInteractiveLessonProgress, id, {
            completed: true,
            currentScreenIndex: currentScreenIndex,
            responses: responsesArray
          });
        } catch (error) {
          console.error('Failed to mark lesson as completed:', error);
        }
      }
    }
  }, [currentScreenIndex, lessonData, id, user, handleRequest, userResponses, screenStates]);

  const handlePrevious = useCallback(() => {
    setShowSolution(false); // Reset solution display when going back
    if (currentScreenIndex > 0) {
      setCurrentScreenIndex(prev => prev - 1);
    }
  }, [currentScreenIndex]);

  const handlePrimaryAction = useCallback(() => {
    const currentScreen = lessonData?.screens[currentScreenIndex];
    if (!currentScreen) return;

    // For content screens, just move to next
    if (currentScreen.type === 'content' || currentScreen.type === 'reflection') {
      handleNext();
      return;
    }

    // For question screens, check if answered
    const screenState = screenStates[currentScreen.id];
    if (screenState?.answered) {
      handleNext();
    }
    // Otherwise, the screen component handles submission
  }, [currentScreenIndex, lessonData, screenStates, handleNext]);

  const handleSecondaryAction = useCallback(async () => {
    // Handle hint or other secondary actions
    const currentScreen = lessonData?.screens[currentScreenIndex];
    const secondaryLabel = currentScreen?.uiLayoutHints?.secondaryButtonLabel?.toLowerCase() || '';
    
    if (secondaryLabel.includes('løsning') || secondaryLabel.includes('solution')) {
      setShowSolution(true);
    } else if (secondaryLabel.includes('hint') || secondaryLabel.includes('hjelp')) {
      // Request hint for coding screens
      if (currentScreen?.type === 'coding' && id) {
        setHintLoading(true);
        try {
          const currentCode = userResponses[currentScreen.id] || '';
          const response = await handleRequest(requestInteractiveHint, id, {
            screenId: currentScreen.id,
            studentCode: currentCode
          });
          
          const newHint = response.data.hint;
          setHints(prev => ({
            ...prev,
            [currentScreen.id]: [
              ...(prev[currentScreen.id] || []),
              newHint
            ]
          }));
        } catch (error) {
          setErrorMessage(error.message || 'Kunne ikke generere hint');
        } finally {
          setHintLoading(false);
        }
      }
    }
  }, [currentScreenIndex, lessonData, id, userResponses, handleRequest]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't trigger navigation if user is typing in an input, textarea, or code editor
      const target = event.target;
      const isInputElement = target.tagName === 'INPUT' || 
                             target.tagName === 'TEXTAREA' || 
                             target.isContentEditable ||
                             target.closest('.monaco-editor') !== null ||
                             target.closest('.code-editor-container') !== null;
      
      if (isInputElement) {
        return; // Don't interfere with typing
      }

      // Calculate navigation state
      const currentScreen = lessonData?.screens[currentScreenIndex];
      const currentScreenState = screenStates[currentScreen?.id];
      const canGoNext = currentScreenIndex < (lessonData?.screens?.length - 1) || showSummary;
      const canGoPrevious = currentScreenIndex > 0;
      const primaryDisabled = currentScreen && 
                               currentScreen.type !== 'content' && 
                               currentScreen.type !== 'reflection' && 
                               !currentScreenState?.answered;

      // Handle keyboard shortcuts
      if (event.key === 'ArrowLeft' && canGoPrevious) {
        handlePrevious();
      } else if (event.key === 'ArrowRight' && canGoNext && !primaryDisabled) {
        event.preventDefault(); // Prevent default browser behavior
        handlePrimaryAction(); // Use primary action to respect screen logic
      } else if ((event.key === ' ' || event.key === 'Spacebar') && canGoNext && !primaryDisabled) {
        event.preventDefault(); // Prevent default scrolling behavior
        handlePrimaryAction(); // Use primary action to respect screen logic
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentScreenIndex, lessonData, screenStates, showSummary, handlePrevious, handlePrimaryAction]);

  if (loading) {
    return (
      <div className="interactive-lesson-container">
        <div className="loading">Loading lesson...</div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="interactive-lesson-container">
        <ErrorCard message={errorMessage} />
        <button onClick={() => navigate('/dashboard')} className="back-button">
          Tilbake til hovedsiden
        </button>
      </div>
    );
  }

  if (!lessonData || !lessonData.screens || lessonData.screens.length === 0) {
    return (
      <div className="interactive-lesson-container">
        <ErrorCard message="Lesson data is invalid or empty" />
        <button onClick={() => navigate('/dashboard')} className="back-button">
          Tilbake til hovedsiden
        </button>
      </div>
    );
  }

  const currentScreen = lessonData.screens[currentScreenIndex];
  const currentScreenState = screenStates[currentScreen.id];
  const isLastScreen = currentScreenIndex === lessonData.screens.length - 1;
  const canGoNext = currentScreenIndex < lessonData.screens.length - 1 || showSummary;
  const canGoPrevious = currentScreenIndex > 0;
  
  // On the last screen, allow completion even if question not answered
  // For other screens, require answer for question screens
  const primaryDisabled = isLastScreen 
    ? false // Always allow completion on last screen
    : (currentScreen.type !== 'content' && 
       currentScreen.type !== 'reflection' && 
       !currentScreenState?.answered);
  
  // On the last screen, show "fullfør" button if not already set
  const primaryButtonLabel = isLastScreen && 
    !currentScreen.uiLayoutHints?.primaryButtonLabel?.toLowerCase().includes('fullfør')
    ? 'Fullfør'
    : (currentScreen.uiLayoutHints?.primaryButtonLabel || 'Next');

  if (showSummary) {
    return (
      <div className="interactive-lesson-container">
        <div className="summary-screen">
          <h2>Leksjonen er fullført!</h2>
          <h3>Nøkkelpoeng</h3>
          <ul>
            {lessonData.summaryScreen.keyTakeaways.map((takeaway, index) => (
              <li key={index}>{takeaway}</li>
            ))}
          </ul>
          <h3>Hva du bør nå gjøre</h3>
          <ul>
            {lessonData.summaryScreen.suggestedNextSteps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ul>
          <button onClick={() => navigate('/dashboard')} className="complete-button">
            Tilbake til hovedsiden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="interactive-lesson-container">
      <LessonTopBar
        currentScreenIndex={currentScreenIndex}
        totalScreens={lessonData.screens.length}
        lessonTitle={lessonData.lessonTitle}
        screenTitle={currentScreen.title}
        progress={(currentScreenIndex + 1) / lessonData.screens.length}
      />

      <div className="lesson-content-area">
        <ScreenRenderer
          screen={currentScreen}
          onAnswer={(answer, result) => handleAnswer(currentScreen.id, answer, result)}
          userResponse={userResponses[currentScreen.id]}
          lessonId={id}
          showSolution={showSolution}
          subjectArea={lesson?.subjectArea}
          screenState={screenStates[currentScreen.id]}
        />
      </div>

      <LessonBottomBar
        primaryButtonLabel={primaryButtonLabel}
        secondaryButtonLabel={currentScreen.uiLayoutHints?.secondaryButtonLabel}
        onPrimaryClick={handlePrimaryAction}
        onSecondaryClick={handleSecondaryAction}
        onPrevious={handlePrevious}
        onNext={handleNext}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        primaryDisabled={primaryDisabled}
        secondaryButtonDisabled={hintLoading}
        showNavigation={true}
      />

      <AITutorPanel
        screenContent={currentScreen.contentForTeacherOrSystem}
        lessonTopic={lesson.topic}
        subjectArea={lesson.subjectArea}
        lessonId={id}
        screenId={currentScreen.id}
      />
    </div>
  );
};

export default InteractiveLesson;
